/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Info, ShieldCheck, ClipboardList, Download } from "lucide-react";
import Layout from "@/components/Layout";
import JurisdictionGrid from "@/components/JurisdictionGrid";
import Disclaimer from "@/components/Disclaimer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { scoreToColor } from "@/lib/scoring";

// Forward-only snapshots shipped on this date. Rows created before it predate
// snapshot capture and cannot be reconstructed (no source of truth exists).
const SHIP_DATE_LABEL = "September 20, 2026";

const STATE_NAMES = {
  AK: "Alaska", AL: "Alabama", AR: "Arkansas", AZ: "Arizona", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "Washington D.C.", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", IA: "Iowa", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  MA: "Massachusetts", MD: "Maryland", ME: "Maine", MI: "Michigan",
  MN: "Minnesota", MO: "Missouri", MS: "Mississippi", MT: "Montana",
  NC: "North Carolina", ND: "North Dakota", NE: "Nebraska", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NV: "Nevada", NY: "New York",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas",
  UT: "Utah", VA: "Virginia", VT: "Vermont", WA: "Washington",
  WI: "Wisconsin", WV: "West Virginia", WY: "Wyoming",
};

const TIER_LABEL = { under_25k: "Under 25k", "25k_to_100k": "25k–100k", "100k_plus": "100k+" };

const fmtBool = (v) => {
  if (v === true || v === "yes") return "Yes";
  if (v === false || v === "no") return "No";
  if (v === "na") return "N/A";
  return "Not answered";
};

const boolTone = (v) => {
  if (v === true || v === "yes") return "var(--rg-sage)";
  if (v === false || v === "no") return "var(--rg-red)";
  return "var(--rg-muted)";
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "Not provided";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toLocaleString("en-US")}`;
};

const fmtText = (v) => {
  if (!v || !String(v).trim()) return "Not provided";
  return `Provided (${String(v).length.toLocaleString("en-US")} characters)`;
};

// A single labeled input row.
const Row = ({ label, value, tone, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-[var(--rg-border)]/60 last:border-0" data-testid={testId}>
    <span className="text-sm text-[var(--rg-text)]">{label}</span>
    <span className="text-sm font-semibold text-right" style={{ color: tone || "var(--rg-text-strong)" }}>{value}</span>
  </div>
);

const Group = ({ title, children, testId }) => (
  <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid={testId}>
    <h3 className="font-heading font-bold text-sm uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-3">{title}</h3>
    <div>{children}</div>
  </div>
);

export default function AuditHistoryDetail() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/audit/me/history/${auditId}`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Unable to load this audit.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [auditId]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="history-detail-loading">
          <Loader2 className="w-8 h-8 text-[var(--rg-sage)] animate-spin mx-auto" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-20" data-testid="history-detail-error">
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-[var(--rg-sage)] mb-6 hover:underline" data-testid="history-detail-back">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <div className="rounded-lg border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-6 text-[var(--rg-text-strong)]">{error}</div>
        </div>
      </Layout>
    );
  }

  const meta = data?.meta || {};
  const inputs = data?.inputs_snapshot || null;
  const report = data?.report_snapshot || null;
  const isLegacy = meta.is_legacy || !report;
  const mode = (meta.score_mode || inputs?.score_mode || "estimated").toLowerCase();
  const modeLabel = mode === "verified" ? "Verified" : "Estimated";

  const selected = inputs?.selected_jurisdictions || [];
  const perState = inputs?.per_state_data || {};
  const perTier = inputs?.per_state_consumer_tier || {};

  const exportPdf = () => {
    // Browser-native print → "Save as PDF". Reuses the app's @media print
    // rules; the detail-page print block stacks the two columns cleanly.
    const prevTitle = document.title;
    const dateStr = (meta.audit_date || "").slice(0, 10);
    document.title = `Regulens-Audit-${dateStr}-Grade-${meta.score_grade || "NA"}`;
    document.body.classList.add("printing");
    window.print();
    setTimeout(() => {
      document.body.classList.remove("printing");
      document.title = prevTitle;
    }, 1500);
  };

  return (
    <Layout fullWidth>
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="history-detail-page">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-[var(--rg-sage)] mb-6 hover:underline" data-testid="history-detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        {/* Header */}
        <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 mb-8" data-testid="history-detail-header">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rg-muted)]">Audit record</span>
            <span
              className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-sm border"
              style={{ color: "var(--rg-sage)", borderColor: "var(--rg-sage)", background: "var(--rg-sage-soft)" }}
              data-testid="history-detail-mode-badge"
            >
              {modeLabel} mode
            </span>
            {!isLegacy && (
              <button
                onClick={exportPdf}
                data-testid="history-export-pdf-btn"
                className="ml-auto inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-md border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-bg)] transition-fast"
              >
                <Download className="w-4 h-4" /> Export as PDF
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-4 gap-6 items-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-1">Date</div>
              <div className="font-mono text-sm text-[var(--rg-text-strong)]" data-testid="history-detail-date">{(meta.audit_date || "").slice(0, 19).replace("T", " ")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-1">Composite score</div>
              <div className="font-heading font-extrabold text-4xl" style={{ color: scoreToColor(meta.composite_score) }} data-testid="history-detail-score">{meta.composite_score}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-1">Grade</div>
              <div className="font-heading font-extrabold text-3xl text-[var(--rg-text-strong)]" data-testid="history-detail-grade">{meta.score_grade}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-1">Trigger</div>
              <div className="text-sm text-[var(--rg-text)]" data-testid="history-detail-trigger">{meta.trigger_reason || "—"}</div>
            </div>
          </div>
        </div>

        {isLegacy ? (
          <div className="rounded-lg border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-6 flex items-start gap-3" data-testid="history-detail-legacy-banner">
            <Info className="w-5 h-5 text-[var(--rg-amber)] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[var(--rg-text-strong)] mb-1">Detail view unavailable for this audit</div>
              <p className="text-sm text-[var(--rg-text)] leading-relaxed">
                Detail view is unavailable for audits before {SHIP_DATE_LABEL} — the score and grade above are preserved. We began recording full input &amp; result snapshots on that date, so any audit run afterward shows the complete breakdown here.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8" data-testid="history-detail-columns">
            {/* LEFT — Inputs at audit time */}
            <div className="space-y-5" data-testid="history-detail-inputs">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[var(--rg-sage)]" />
                <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)]">Inputs at audit time</h2>
              </div>

              <Group title="Sales Tax Nexus" testId="history-inputs-pillar1">
                <Row label="Score mode" value={modeLabel} testId="hi-score-mode" />
                <Row label="Revenue tier" value={inputs?.annual_revenue_tier || "Not provided"} testId="hi-rev-tier" />
                <Row label="Exact annual revenue" value={fmtMoney(inputs?.annual_revenue_exact)} testId="hi-rev-exact" />
                <Row label="Transaction tier" value={inputs?.annual_transaction_tier || "Not provided"} testId="hi-txn-tier" />
                <Row label="Selected jurisdictions" value={selected.length ? selected.join(", ") : "None"} testId="hi-selected" />
                <Row label="Nexus documented" value={fmtBool(inputs?.nexus_documented)} tone={boolTone(inputs?.nexus_documented)} testId="hi-nexus-doc" />
                <Row label="Jurisdictions identified" value={fmtBool(inputs?.jurisdictions_identified)} tone={boolTone(inputs?.jurisdictions_identified)} testId="hi-juris-identified" />
                {mode === "verified" && Object.keys(perState).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--rg-border)]" data-testid="hi-per-state">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-2">Per-state figures (Verified)</div>
                    {Object.entries(perState).map(([abbr, d]) => (
                      <Row
                        key={abbr}
                        label={`${STATE_NAMES[abbr] || abbr} (${abbr})`}
                        value={`Rev ${fmtMoney(d?.revenue)} · ${d?.transactions ?? "—"} txns${d?.consumers != null ? ` · ${Number(d.consumers).toLocaleString("en-US")} consumers` : ""}`}
                        testId={`hi-per-state-${abbr}`}
                      />
                    ))}
                  </div>
                )}
              </Group>

              <Group title="Privacy Law Readiness" testId="history-inputs-pillar2">
                <Row label="Privacy policy text" value={fmtText(inputs?.privacy_policy_text)} testId="hi-privacy-text" />
                <Row label="Cookie consent present" value={fmtBool(inputs?.cookie_consent_present)} tone={boolTone(inputs?.cookie_consent_present)} testId="hi-cookie" />
                <Row label="Privacy contact present" value={fmtBool(inputs?.privacy_contact_present)} tone={boolTone(inputs?.privacy_contact_present)} testId="hi-privacy-contact" />
                <Row label="Retention policy present" value={fmtBool(inputs?.retention_policy_present)} tone={boolTone(inputs?.retention_policy_present)} testId="hi-retention" />
                <Row label="Right to correction" value={fmtBool(inputs?.has_correction)} tone={boolTone(inputs?.has_correction)} testId="hi-correction" />
                <Row label="Right to portability" value={fmtBool(inputs?.has_portability)} tone={boolTone(inputs?.has_portability)} testId="hi-portability" />
                <Row label="Sensitive-data disclosure" value={fmtBool(inputs?.has_sensitive_data_disclosure)} tone={boolTone(inputs?.has_sensitive_data_disclosure)} testId="hi-sensitive" />
                <Row label="Sells / shares personal data" value={fmtBool(inputs?.sells_personal_data)} tone={boolTone(inputs?.sells_personal_data)} testId="hi-sells" />
                <Row label="SBA small business (NE)" value={fmtBool(inputs?.is_sba_small_business)} tone={boolTone(inputs?.is_sba_small_business)} testId="hi-sba" />
                {Object.keys(perTier).length > 0 && (
                  <Row label="Consumer tiers (Estimated)" value={Object.entries(perTier).map(([a, t]) => `${a}: ${TIER_LABEL[t] || t}`).join(", ")} testId="hi-consumer-tiers" />
                )}
              </Group>

              {(inputs?.collects_biometric_data != null || inputs?.collects_health_data != null) && (
                <Group title="Sector-Specific Signals" testId="history-inputs-sector">
                  {inputs?.collects_biometric_data != null && (
                    <>
                      <Row label="Collects biometric data (IL BIPA)" value={fmtBool(inputs?.collects_biometric_data)} tone={boolTone(inputs?.collects_biometric_data)} testId="hi-biometric" />
                      <Row label="Biometric consent" value={fmtBool(inputs?.has_biometric_consent)} tone={boolTone(inputs?.has_biometric_consent)} testId="hi-biometric-consent" />
                      <Row label="Biometric retention schedule" value={fmtBool(inputs?.has_biometric_retention_schedule)} tone={boolTone(inputs?.has_biometric_retention_schedule)} testId="hi-biometric-retention" />
                      <Row label="Biometric no-sale" value={fmtBool(inputs?.has_biometric_no_sale)} tone={boolTone(inputs?.has_biometric_no_sale)} testId="hi-biometric-nosale" />
                    </>
                  )}
                  {inputs?.collects_health_data != null && (
                    <>
                      <Row label="Collects health data (WA MHMDA)" value={fmtBool(inputs?.collects_health_data)} tone={boolTone(inputs?.collects_health_data)} testId="hi-health" />
                      <Row label="MHMDA-specific policy" value={fmtBool(inputs?.has_mhmda_specific_policy)} tone={boolTone(inputs?.has_mhmda_specific_policy)} testId="hi-mhmda-policy" />
                      <Row label="Opt-in consent" value={fmtBool(inputs?.has_health_data_opt_in_consent)} tone={boolTone(inputs?.has_health_data_opt_in_consent)} testId="hi-health-optin" />
                      <Row label="Sale authorization" value={fmtBool(inputs?.has_health_data_sale_authorization)} tone={boolTone(inputs?.has_health_data_sale_authorization)} testId="hi-health-sale" />
                      <Row label="Withdrawal right" value={fmtBool(inputs?.has_health_data_withdrawal_right)} tone={boolTone(inputs?.has_health_data_withdrawal_right)} testId="hi-health-withdraw" />
                      <Row label="Deletion right" value={fmtBool(inputs?.has_health_data_deletion_right)} tone={boolTone(inputs?.has_health_data_deletion_right)} testId="hi-health-delete" />
                    </>
                  )}
                </Group>
              )}

              <Group title="Policy Hygiene & Monitoring" testId="history-inputs-pillar34">
                <Row label="Terms of service text" value={fmtText(inputs?.terms_of_service_text)} testId="hi-tos-text" />
                <Row label="Refund policy text" value={fmtText(inputs?.refund_policy_text)} testId="hi-refund-text" />
                <Row label="Active monitoring" value={fmtBool(inputs?.active_monitoring)} tone={boolTone(inputs?.active_monitoring)} testId="hi-monitoring" />
                <Row label="Operational focus" value={inputs?.operational_focus || "Not provided"} testId="hi-focus" />
                <Row label="Store owner verified" value={fmtBool(inputs?.verified_store_owner)} tone={boolTone(inputs?.verified_store_owner)} testId="hi-verified-owner" />
              </Group>
            </div>

            {/* RIGHT — Result at audit time */}
            <div className="space-y-5" data-testid="history-detail-result">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[var(--rg-sage)]" />
                <h2 className="font-heading font-bold text-xl text-[var(--rg-text-strong)]">Result at audit time</h2>
              </div>
              {report?.executiveSummary && (
                <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="history-detail-summary">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--rg-muted)] mb-2">Executive summary</div>
                  <p className="text-sm text-[var(--rg-text)] leading-relaxed whitespace-pre-line">{report.executiveSummary}</p>
                </div>
              )}
              <JurisdictionGrid
                analysis={report?.jurisdictionAnalysis || {}}
                verifiedSources={report?.verifiedSources || {}}
                testIdPrefix="history-juris"
                perStateConsumerTier={perTier}
                scoreMode={mode}
              />
            </div>
          </div>
        )}

        <div className="mt-10">
          <Disclaimer />
        </div>
      </div>
    </Layout>
  );
}
